import { ReactNode } from 'react';
import { IntegrationProvider } from '@momentum/types';
import {
  AsanaLogo,
  ConfluenceLogo,
  DynamicsCrmLogo,
  GitHubLogo,
  GoogleDriveLogo,
  HubSpotLogo,
  JiraLogo,
  LinearLogo,
  MicrosoftTeamsLogo,
  MondayLogo,
  NotionLogo,
  SalesforceLogo,
  SharePointLogo,
  SlackLogo,
  ZendeskLogo,
  ZoomLogo,
} from '@/features/goals/integrations/components/integration-logos';

export type ProviderI18nKey =
  | 'sharepoint'
  | 'dynamicsCrm'
  | 'jira'
  | 'confluence'
  | 'slack'
  | 'salesforce'
  | 'microsoftTeams'
  | 'googleDrive'
  | 'notion'
  | 'github'
  | 'hubspot'
  | 'asana'
  | 'linear'
  | 'zoom'
  | 'zendesk'
  | 'monday';

export interface ProviderCardConfig {
  i18nKey: ProviderI18nKey;
  /**
   * Set only when the card maps to a real API-side `IntegrationProvider`.
   * Used to match against `connectedProviders` from the onboarding status endpoint.
   * Cards without it cannot show a "Connected" state.
   */
  providerEnum?: IntegrationProvider;
  logo: () => ReactNode;
  live: boolean;
  providerConfigKey?: string;
  tint: string;
  logoTint: string;
}

export const PROVIDER_CARDS: ProviderCardConfig[] = [
  {
    i18nKey: 'sharepoint',
    providerEnum: IntegrationProvider.SHAREPOINT,
    logo: SharePointLogo,
    live: true,
    providerConfigKey: 'sharepoint-online',
    tint: 'border-[#0F6CBD]/20 hover:border-[#0F6CBD]/40',
    logoTint: 'bg-[#0F6CBD]/[0.04] ring-[#0F6CBD]/15',
  },
  {
    i18nKey: 'dynamicsCrm',
    providerEnum: IntegrationProvider.DYNAMICS_CRM,
    logo: DynamicsCrmLogo,
    live: true,
    providerConfigKey: 'microsoft-tenant-specific',
    tint: 'border-[#002050]/20 hover:border-[#002050]/40',
    logoTint: 'bg-[#002050]/[0.04] ring-[#002050]/15',
  },
  {
    i18nKey: 'jira',
    providerEnum: IntegrationProvider.JIRA,
    logo: JiraLogo,
    live: true,
    providerConfigKey: 'jira',
    tint: 'border-[#0052CC]/20 hover:border-[#0052CC]/40',
    logoTint: 'bg-[#0052CC]/[0.04] ring-[#0052CC]/15',
  },
  {
    i18nKey: 'confluence',
    providerEnum: IntegrationProvider.CONFLUENCE,
    logo: ConfluenceLogo,
    live: true,
    providerConfigKey: 'confluence',
    tint: 'border-[#1868DB]/20 hover:border-[#1868DB]/40',
    logoTint: 'bg-[#1868DB]/[0.04] ring-[#1868DB]/15',
  },
  {
    i18nKey: 'microsoftTeams',
    logo: MicrosoftTeamsLogo,
    live: true,
    providerConfigKey: 'microsoft-teams-placeholder',
    tint: 'border-[#4B53BC]/20 hover:border-[#4B53BC]/40',
    logoTint: 'bg-[#4B53BC]/[0.04] ring-[#4B53BC]/15',
  },
  {
    i18nKey: 'googleDrive',
    logo: GoogleDriveLogo,
    live: true,
    providerConfigKey: 'google-drive-placeholder',
    tint: 'border-[#1A73E8]/20 hover:border-[#1A73E8]/40',
    logoTint: 'bg-[#1A73E8]/[0.04] ring-[#1A73E8]/15',
  },
  {
    i18nKey: 'notion',
    logo: NotionLogo,
    live: true,
    providerConfigKey: 'notion-placeholder',
    tint: 'border-[#334155]/20 hover:border-[#334155]/40',
    logoTint: 'bg-[#334155]/[0.04] ring-[#334155]/15',
  },
  {
    i18nKey: 'github',
    logo: GitHubLogo,
    live: true,
    providerConfigKey: 'github-placeholder',
    tint: 'border-[#1E293B]/20 hover:border-[#1E293B]/40',
    logoTint: 'bg-[#1E293B]/[0.04] ring-[#1E293B]/15',
  },
  {
    i18nKey: 'hubspot',
    logo: HubSpotLogo,
    live: true,
    providerConfigKey: 'hubspot-placeholder',
    tint: 'border-[#FF7A59]/25 hover:border-[#FF7A59]/45',
    logoTint: 'bg-[#FF7A59]/[0.05] ring-[#FF7A59]/15',
  },
  {
    i18nKey: 'asana',
    logo: AsanaLogo,
    live: true,
    providerConfigKey: 'asana-placeholder',
    tint: 'border-[#F06A6A]/25 hover:border-[#F06A6A]/45',
    logoTint: 'bg-[#F06A6A]/[0.05] ring-[#F06A6A]/15',
  },
  {
    i18nKey: 'linear',
    logo: LinearLogo,
    live: true,
    providerConfigKey: 'linear-placeholder',
    tint: 'border-[#5E6AD2]/20 hover:border-[#5E6AD2]/40',
    logoTint: 'bg-[#5E6AD2]/[0.04] ring-[#5E6AD2]/15',
  },
  {
    i18nKey: 'zoom',
    logo: ZoomLogo,
    live: true,
    providerConfigKey: 'zoom-placeholder',
    tint: 'border-[#0B5CFF]/20 hover:border-[#0B5CFF]/40',
    logoTint: 'bg-[#0B5CFF]/[0.04] ring-[#0B5CFF]/15',
  },
  {
    i18nKey: 'zendesk',
    logo: ZendeskLogo,
    live: true,
    providerConfigKey: 'zendesk-placeholder',
    tint: 'border-[#03363D]/20 hover:border-[#03363D]/40',
    logoTint: 'bg-[#03363D]/[0.04] ring-[#03363D]/15',
  },
  {
    i18nKey: 'monday',
    logo: MondayLogo,
    live: true,
    providerConfigKey: 'monday-placeholder',
    tint: 'border-[#FF3D57]/25 hover:border-[#FF3D57]/45',
    logoTint: 'bg-[#FF3D57]/[0.05] ring-[#FF3D57]/15',
  },
  {
    i18nKey: 'slack',
    logo: SlackLogo,
    live: false,
    tint: 'border-dashed border-[#4A154B]/30',
    logoTint: 'bg-[#4A154B]/[0.04] ring-[#4A154B]/15',
  },
  {
    i18nKey: 'salesforce',
    logo: SalesforceLogo,
    live: false,
    tint: 'border-dashed border-[#00A1E0]/30',
    logoTint: 'bg-[#00A1E0]/[0.04] ring-[#00A1E0]/15',
  },
];
